import { constants } from 'lib_ui-services';
import basicWillExport from '../../namespace/relation/export_namespace_relation/willExport_namespace_relation';
import { UserError } from '@sstdev/lib_logging';
import resolvePhysicalInventoryId from '../../../../utilities/resolvePhysicalInventoryId';
import AMI008_COLUMN_DEFINITIONS from '../../../../utilities/ami008Columns';
import { format } from 'date-fns';
import lodash from 'lodash';
const { get, set } = lodash;
const { notificationTypes } = constants;
// define this here to keep any reference to AMI008 contained to this one file.
const AMI008 = {
    _id: '6',
    title: 'AMI008',
    contentType: 'text/csv',
    description: 'PALM Inbound Asset Physical Inventory CSV File (AMI008)',
    fileExt: '.csv',
    bookType: 'csv',
    forceQuotes: true,
    doNotEscapeQuotes: false
};

const [major, minor, patch] = __PACKAGE_VERSION__.split('.'); //strip any -beta, -rc, etc
const PACKAGE_VERSION = `${major}.${minor}.${patch}`;

export default {
    verb: 'willExport',
    namespace: 'item',
    relation: 'item',
    description: 'Intercept export definition to tweak for AMI008',
    // we want this to run AFTER the default willExport, letting it take care
    // of communicating with the user, etc
    priority: (basicWillExport.priority || 0) - 5,
    useCaseIds: [constants.useCaseIds.PALM],
    prerequisites: [
        {
            context: {
                verb: 'get',
                namespace: 'item',
                relation: 'item',
                type: 'find'
            },
            query: ({ data }) => {
                if (data?.filePrefix?.toUpperCase() !== AMI008.title) {
                    //avoid lookup, as lookup will happen later. Throw error to abort prerequisite workflow
                    // (does not abort entire workflow!)
                    throw new UserError('Request is not for AMI008');
                }
                return {
                    'meta.deleted': { $exists: false }
                };
            }
        },
        // The AMI008 file has to be named after the physical inventory it reports on.
        // Appended rather than prepended on purpose: prerequisite results are positional, so
        // `prerequisiteResults[0]` must stay the items above.
        {
            context: {
                verb: 'get',
                namespace: 'inventory',
                relation: 'inventory',
                type: 'find'
            },
            query: ({ data }) => {
                if (data?.filePrefix?.toUpperCase() !== AMI008.title) {
                    // Same guard as above, so an ordinary item export does not pay for an
                    // inventory lookup it will never use.
                    throw new UserError('Request is not for AMI008');
                }
                return {
                    'inventory:status.title': 'Active'
                };
            }
        }
    ],
    logic: willExport,
    onError
};

async function willExport({ data, prerequisiteResults, context, dispatch, ...rest }) {
    const filePrefix = data?.filePrefix || '';
    if (filePrefix.toUpperCase() !== AMI008.title) {
        return { data, ...rest, context };
    }

    const prerequisiteResult = prerequisiteResults[0];
    const result = prerequisiteResult?.result ?? [];
    const { physicalInventoryId, error } = resolvePhysicalInventoryId({
        navigationSelection: data.navigationSelection,
        activeInventories: prerequisiteResults[1]?.result ?? [],
        items: result
    });
    // A thrown error would not reach the user: this rule's failure event is consumed only to clear
    // the button's busy state, and the workflow's event sink swallows UserErrors. The "Export
    // failed" toast belongs to willExport_namespace_relation, a different step, so it never runs
    // for an error raised here. Notify explicitly, then abort the way
    // willExport_item_item-patch does.
    if (error) {
        await sendNotification(error, 'warning', dispatch);
        return abort(context, dispatch);
    }
    const { prefixCode = 'DXX' } = context?.session?.tenant || {};

    // Do we need to prompt here for Business Unit?

    // add a MATCH_STATUS and REVIEW_SW field to each record
    // MATCH_STATUS is their way of recording the inventory state:
    // O if it was found, but not originally part of the inventory
    // U if it was part of the inventory but not found
    // I if it was part of the inventory and found
    // REVIEW_SW: Review In Software (or something like that)
    // N: When scanned with a scanner
    // Y: When manually entered
    const records = result.map(x => {
        if (x.inventory?.[0]?.isNew) {
            x.matchStatus = 'O'; // "Over"
        } else if (x.inventory?.[0]?.found) {
            x.matchStatus = 'I'; // "Inventory"
        } else {
            x.matchStatus = 'U'; // "Under"
        }
        x.reviewSW = x.inventory?.[0]?.foundByScan ? 'N' : 'Y';

        //  format x.inventory[0].foundDate to "MM/DD/YYYY HH:mm:ss.fff000000 AM" in EST time zone
        if (x.inventory?.[0]?.foundDate) {
            // get* methods use the local time of the browser, so for this file (PALM) this will all be in EST)
            const foundDate = new Date(x.inventory[0].foundDate);
            const month = String(foundDate.getMonth() + 1).padStart(2, '0');
            const day = String(foundDate.getDate()).padStart(2, '0');
            const year = foundDate.getFullYear();
            let hours = foundDate.getHours();
            const minutes = String(foundDate.getMinutes()).padStart(2, '0');
            const seconds = String(foundDate.getSeconds()).padStart(2, '0');
            const milliseconds = String(foundDate.getMilliseconds()).padStart(3, '0');
            const ampm = hours >= 12 ? 'PM' : 'AM';
            hours = hours % 12;
            hours = hours ? hours : 12; // the hour '0' should be '12'
            const strHours = String(hours).padStart(2, '0');
            x.LAST_SCAN_DTTM = `${month}/${day}/${year} ${strHours}:${minutes}:${seconds}.${milliseconds}000000 ${ampm}`;
        }

        x.softwareVersion = PACKAGE_VERSION;

        // assure all fields with propertynames in AMI008_COLUMN_DEFINITIONS exist
        // if they don't exist, set them to empty string
        AMI008_COLUMN_DEFINITIONS.forEach(colDef => {
            const propName = colDef.propertyName;
            // support nested property names using dot as separator
            let value = get(x, propName);
            if (isBlank(value) && colDef.legacyPropertyName) {
                // An item imported before #1423 moved this column onto a lookup relation still
                // holds the old flat path. Read it from there rather than exporting a blank
                // column for an inventory that is already in flight.
                value = get(x, colDef.legacyPropertyName);
            }
            set(x, propName, isBlank(value) ? ' ' : value);
        });

        return x;
    });

    // set the file type definition, as well as stuff in properly prepared data
    // if we don't set it a file type, it will export to .xlsx by default, which we don't want.

    const timestamp = format(new Date(), "yyyyMMdd'-'HHmmss");
    const fileName = `${prefixCode}_AMI008_O_${physicalInventoryId}_${timestamp}`;
    const newData = {
        ...data,
        namespace: 'item',
        relation: 'item',
        filePrefix: AMI008.title,
        fileName,
        fileType: AMI008,
        data: records,
        columns: AMI008_COLUMN_DEFINITIONS
    };

    return { data: newData, ...rest, context };
}

/**
 * A column with nothing in it. PALM reads a single space as empty, so a value of ' ' that is
 * already on the record counts as present - only a missing one is filled in.
 *
 * @param {unknown} value
 */
function isBlank(value) {
    return value === undefined || value === null || value === '';
}

/**
 * When aborting, the rules engine short-circuits without dispatching a status event. Dispatching
 * status:'failure' keeps useExportingState and showBusyUntilFinished buttons in step, so the export
 * button is re-enabled.
 *
 * @param {import("rulesengine.io").Context} context
 * @param {(data:object,context:object,awaitResult?:boolean)=>Promise<any>} dispatch
 */
async function abort(context, dispatch) {
    await dispatch({ errors: { field: {}, form: [] } }, { ...context, status: 'failure' });
    return { error: { field: {}, aborted: true }, data: {} };
}

/**
 * @param {string} message
 * @param {'warning'|'error'} variant
 * @param {(data:object,context:object,awaitResult?:boolean)=>Promise<any>} dispatch
 */
async function sendNotification(message, variant, dispatch) {
    await dispatch(
        { addToList: false, message, variant, type: notificationTypes.TOAST },
        { verb: 'pop', namespace: 'application', relation: 'notification' }
    );
}

/**
 * Safety net for anything unexpected thrown from this rule, which would otherwise leave the user
 * with a button that stops spinning and no explanation.
 *
 * @param {{error: Error, dispatch: (data:object,context:object,awaitResult?:boolean)=>Promise<any>}} parameters
 */
function onError({ error, dispatch }) {
    sendNotification(`Export failed: ${error.message}`, 'error', dispatch);
    throw error;
}
